import React, { InputHTMLAttributes, ReactNode, useState } from 'react';
import { FieldError } from 'react-hook-form';

import { RegularChevronDown } from '@/icons/components';
import { twJoin } from '@/utils/tw-merge';

import { FormError } from '../FormError';
import { FormLabel } from '../FormLabel';

export interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  id: string;
  name: string;
  label: string;
  subtitle?: string;
  hideLabel?: boolean;
  required?: boolean;
  labelClasses?: string;
  inputClasses?: string;
  /** Error coming from `react-hook-form` */
  error?: FieldError;
  disabled?: boolean;
  children: ReactNode;
  ref?: React.Ref<HTMLSelectElement>;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id,
      name,
      label,
      hideLabel,
      className,
      labelClasses,
      inputClasses,
      required,
      error,
      disabled,
      children,
      onChange,
      subtitle,
      ...props
    },
    ref
  ) => {
    const [hasValue, setHasValue] = useState<boolean>(!!props.defaultValue);

    const errorId = `${id}-error`;

    return (
      <div className={className}>
        <FormLabel
          htmlFor={id}
          className={twJoin('mb-1', hideLabel && 'sr-only', labelClasses)}
          required={required}
        >
          {label}
        </FormLabel>

        {subtitle && (
          <p className="style-input-label mb-1 text-gray-800">{subtitle}</p>
        )}

        <div className="relative has-[:disabled]:text-gray-600">
          <select
            id={id}
            name={name}
            ref={ref}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            onChange={(e) => {
              setHasValue(!!e.target.value);
              onChange?.(e);
            }}
            aria-describedby={error ? errorId : undefined}
            className={twJoin(
              'style-input-label peer h-5 w-full appearance-none rounded-sm border border-gray-500 bg-white pl-2 pr-4 transition-all',
              'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
              'placeholder:text-placeholder',
              'disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-600 disabled:opacity-100',
              error &&
                '-mb-px !rounded-b-none !border-red-500 !text-red-800 focus:!ring-red-500',
              !hasValue && 'text-gray-500',
              hasValue && 'text-black',
              inputClasses
            )}
            {...props}
          >
            {children}
          </select>

          <RegularChevronDown
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
            size={14}
          />
        </div>

        {error && (
          <FormError id={errorId} className="!rounded-t-none">
            {error.message}
          </FormError>
        )}
      </div>
    );
  }
);
Select.displayName = 'Select';
